import { useTranslation } from "react-i18next";

import { CircleCheckIcon, ExternalLinkIcon } from "lucide-react";

import { Card, CardContent } from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Message } from "simplydo/react/ui/message";

import CompaniesHouseIcon from "src/images/companiesHouse-icon.png";

import type { CompaniesHouseCompany, IdeaBusinessProfile } from "../types";
import { isCreditsafeVatVerified } from "./identifierLookup";

type CompaniesHouseCheckCardProps = { businessProfile: IdeaBusinessProfile };

export const CompaniesHouseCheckCard = ({
  businessProfile,
}: CompaniesHouseCheckCardProps) => {
  const company = businessProfile.companiesHouse?.company as
    | CompaniesHouseCompany
    | undefined;

  if (
    businessProfile.creditsafeRegNoSkipped ||
    !businessProfile.creditsafeRegNo ||
    !businessProfile.companiesHouse?.isAvailable ||
    !company?.company_name
  ) {
    return null;
  }

  if (
    businessProfile.companiesHouse.companyNumber !==
    businessProfile.creditsafeRegNo
  ) {
    return null;
  }

  return (
    <Card fluid placeholder elevation="small" style={{ marginTop: "0.5rem" }}>
      <CardContent>
        <b>Companies House check</b>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 8,
          }}
        >
          <Image
            src={CompaniesHouseIcon}
            style={{ width: 40, height: 40, objectFit: "contain" }}
          />
          <Icon icon={CircleCheckIcon} color="blue" size="large" />
          <div>
            The company number &quot;
            {businessProfile.creditsafeRegNo}
            &quot; is registered as &quot;
            {company.company_name}
            &quot;
            {company.links?.self ? (
              <>
                <br />
                <a
                  href={`https://beta.companieshouse.gov.uk${company.links.self}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View company profile on Companies House
                  <Icon
                    icon={ExternalLinkIcon}
                    size="small"
                    style={{ marginLeft: 2 }}
                  />
                </a>
              </>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

type VatNumberCheckCardProps = { businessProfile: IdeaBusinessProfile };

export const VatNumberCheckCard = ({
  businessProfile,
}: VatNumberCheckCardProps) => {
  const { t } = useTranslation();
  const check = businessProfile.creditsafe;
  const companyName = check?.company?.name;

  if (!isCreditsafeVatVerified(businessProfile) || !companyName) {
    return null;
  }

  return (
    <Card fluid placeholder elevation="small" style={{ marginTop: "0.5rem" }}>
      <CardContent>
        <b>{t("ideaBusinessProfiles.vatCheck.title")}</b>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 8,
          }}
        >
          <Icon icon={CircleCheckIcon} color="blue" size="large" />
          <div>
            {t("ideaBusinessProfiles.vatCheck.matched", {
              vatNo: businessProfile.vatNo,
              name: companyName,
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
